// hypothes.is is a third-party annotation tool embedded via a single <script> tag; its
// sidebar and text-selection popup are its own markup, not Silverchair's. This file adds
// accessibility behavior on top of that markup without modifying hypothes.is itself.
var SCM = SCM || {};

SCM.HypothesisAccessibility = (function ($) {
    const SidebarTagName = 'HYPOTHESIS-SIDEBAR';
    const AdderTagName = 'HYPOTHESIS-ADDER';
    const ToggleButtonSelector = '[aria-controls="sidebar-container"]';
    const ObserverSafetyTimeoutMs = 15000;
    const ShadowRootRetryFrames = 10;

    return {
        init: _init
    };

    function _init() {
        _waitForElement(SidebarTagName, _wireSidebar);
        _waitForElement(AdderTagName, _addShortcutHintStyles);
    }

    // hypothes.is renders the same custom elements whether its embed script is loaded through
    // the platform's Hypothesis widget or injected some other way (e.g. tag manager), so
    // watching the DOM directly covers every loading path without depending on widget config.
    function _waitForElement(tagName, onFound) {
        const existing = document.body.querySelector(tagName.toLowerCase());
        if (existing) {
            onFound(existing);
            return;
        }

        const observer = new MutationObserver(function () {
            const found = document.body.querySelector(tagName.toLowerCase());
            if (found) {
                observer.disconnect();
                onFound(found);
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        setTimeout(function () {
            observer.disconnect();
        }, ObserverSafetyTimeoutMs);
    }

    // Custom elements normally attach their shadow root synchronously in the constructor,
    // so it's already present by the time _waitForElement hands us the host node - but
    // nothing guarantees that stays true if hypothes.is ever changes how it attaches.
    // There's no mutation event for shadow root attachment, so a bounded rAF poll is the
    // only way to wait it out instead of silently giving up on the first check.
    function _withShadowRoot(hostEl, onReady, framesRemaining) {
        if (hostEl.shadowRoot) {
            onReady(hostEl.shadowRoot);
            return;
        }

        framesRemaining = framesRemaining === undefined ? ShadowRootRetryFrames : framesRemaining;
        if (framesRemaining <= 0) {
            return;
        }

        requestAnimationFrame(function () {
            _withShadowRoot(hostEl, onReady, framesRemaining - 1);
        });
    }

    function _wireSidebar(sidebarEl) {
        // Tracks whether the most recent Tab keydown originated from inside the sidebar -
        // not just whether it was a Tab press. Without this, tabbing anywhere else on the
        // page while the sidebar happens to be open (e.g. opened by mouse, then reading
        // elsewhere) would also close it, since only the incoming focus target was checked.
        let lastTabKeydownWasWithinSidebar = false;

        $(document).on('keydown.hypothesisAccessibility', function (e) {
            if (e.key !== 'Tab') {
                lastTabKeydownWasWithinSidebar = false;
                return;
            }
            const path = e.originalEvent && e.originalEvent.composedPath ? e.originalEvent.composedPath() : [];
            lastTabKeydownWasWithinSidebar = path.indexOf(sidebarEl) !== -1;
        });
        $(document).on('mousedown.hypothesisAccessibility', function () {
            lastTabKeydownWasWithinSidebar = false;
        });

        // Scoped to composedPath, like the Tab tracking above: only treat Escape as "close
        // the sidebar" when it actually originates from within the sidebar. Checking
        // aria-expanded alone (ignoring where focus is) would also swallow Escape for any
        // other document-level Escape handler on the page (e.g. SCM.Utilities.Reveal's
        // modals, SCM.Dropdowns) any time the sidebar happened to be open elsewhere,
        // regardless of what the user was actually trying to close.
        $(document).on('keydown.hypothesisAccessibility', function (e) {
            if (e.key !== 'Escape') {
                return;
            }
            const toggleButton = _getToggleButton(sidebarEl);
            if (!toggleButton || toggleButton.getAttribute('aria-expanded') !== 'true') {
                return;
            }
            const path = e.originalEvent && e.originalEvent.composedPath ? e.originalEvent.composedPath() : [];
            if (path.indexOf(sidebarEl) === -1) {
                return;
            }
            e.preventDefault();
            _closeSidebar(toggleButton);
        });

        $(document).on('focusin.hypothesisAccessibility', function (e) {
            // Reset unconditionally, not just when the guard below passes - otherwise a
            // guard short-circuit (toggle button momentarily missing, aria-expanded already
            // false) would leave a stale "true" that a later, unrelated focus event could
            // act on.
            const wasTabFromSidebar = lastTabKeydownWasWithinSidebar;
            lastTabKeydownWasWithinSidebar = false;

            const toggleButton = _getToggleButton(sidebarEl);
            if (!toggleButton || !wasTabFromSidebar || toggleButton.getAttribute('aria-expanded') !== 'true') {
                return;
            }

            const path = e.originalEvent && e.originalEvent.composedPath ? e.originalEvent.composedPath() : [];
            if (path.indexOf(sidebarEl) !== -1) {
                return; // focus moved to another element within the sidebar
            }

            _closeSidebar(toggleButton);
        });
    }

    function _getToggleButton(sidebarEl) {
        return sidebarEl.shadowRoot ? sidebarEl.shadowRoot.querySelector(ToggleButtonSelector) : null;
    }

    function _closeSidebar(toggleButton) {
        const $trigger = $('.js-annotate:visible').first();
        // Not toggleButton.click(): that dispatches a bubbling, composed click that reaches
        // document.body, where SCM.Dropdowns closes every open dropdown on any outside click -
        // entirely separate from its Escape handler. A non-bubbling click still reaches
        // hypothes.is's own listener on the button but never leaves it.
        toggleButton.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true }));

        if ($trigger.length) {
            $trigger.trigger('focus');
        } else {
            $(toggleButton).trigger('focus');
        }
    }

    // hypothes.is already activates Annotate/Highlight via "a"/"h" the moment the popup is
    // visible, independent of focus - it just never shows that the shortcut exists. This adds
    // a persistent visual badge so a keyboard user can discover it. The CSS lives in the
    // adder's own shadow root, so it keeps applying across hypothes.is's own re-renders -
    // only the elements it manages get replaced, a sibling <style> tag is left alone.
    function _addShortcutHintStyles(adderEl) {
        _withShadowRoot(adderEl, function (shadowRoot) {
            const styleEl = document.createElement('style');
            styleEl.textContent =
                'button[aria-label^="Annotate"], button[aria-label^="Highlight"] { position: relative; }' +
                'button[aria-label^="Annotate"]::after, button[aria-label^="Highlight"]::after {' +
                'position: absolute; top: 2px; right: 2px; font-size: 9px; font-weight: 700; line-height: 1;' +
                'color: #fff; background: #444; border-radius: 2px; padding: 1px 3px; pointer-events: none; }' +
                'button[aria-label^="Annotate"]::after { content: "A"; }' +
                'button[aria-label^="Highlight"]::after { content: "H"; }';
            shadowRoot.appendChild(styleEl);
        });
    }

})(jQuery);
